import React from "react";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
} from "@mui/material";

// third party
import * as Yup from "yup";
import { Formik } from "formik";

// project imports
import AnimateButton from "@stories/ui-component/extended/AnimateButton";
import useScriptRef from "@hooks/utils/useScriptRef";

// assets
import { useAuthContext } from "@hooks/context/useAuthContext";
import { ToastOptions, toast } from "react-toastify";
import { StyledButton } from "@stories/atoms/StyledButton/StyledButton";

// ===============================|| JWT LOGIN ||=============================== //

const LoginForm = ({ loginProp, ...others }: { loginProp?: number }) => {
  const theme = useTheme();

  const { authenticate } = useAuthContext();
  const scriptedRef = useScriptRef();

  const [checked, setChecked] = React.useState(true);

  return (
    <Formik
      initialValues={{
        email: "",
        submit: null,
      }}
      validationSchema={Yup.object().shape({
        email: Yup.string()
          .email("Must be a valid email")
          .max(255)
          .required("Email is required"),
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await authenticate(values.email);

          setStatus({ success: true });
          setSubmitting(false);

          toast(`Login email sent to ${values.email}`, {
            type: "success",
          } as ToastOptions);
        } catch (err: any) {
          console.error(err);
          setStatus({ success: false });
          setErrors({ submit: err.message });
          setSubmitting(false);
          toast("Something went wrong!", { type: "error" } as ToastOptions);
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <form noValidate onSubmit={handleSubmit} {...others}>
          <FormControl
            fullWidth
            error={Boolean(touched.email && errors.email)}
            sx={{ ...theme.typography.customInput }}
          >
            <InputLabel htmlFor="outlined-adornment-email-login">
              Email Address
            </InputLabel>
            <OutlinedInput
              id="outlined-adornment-email-login"
              type="email"
              value={values.email}
              name="email"
              onBlur={handleBlur}
              onChange={handleChange}
              inputProps={{}}
              placeholder="info@socialproapp.com"
            />
            {touched.email && errors.email && (
              <FormHelperText
                error
                id="standard-weight-helper-text-email-login"
              >
                {errors.email}
              </FormHelperText>
            )}
          </FormControl>

          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checked}
                    onChange={(event) => setChecked(event.target.checked)}
                    name="checked"
                    color="primary"
                  />
                }
                label="Keep me logged in"
              />
            </Grid>
          </Grid>

          {errors.submit && (
            <Box sx={{ mt: 3 }}>
              <FormHelperText error>{errors.submit}</FormHelperText>
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <AnimateButton>
              <StyledButton disabled={isSubmitting} fullWidth type="submit">
                Sign In
              </StyledButton>
            </AnimateButton>
          </Box>
        </form>
      )}
    </Formik>
  );
};

export default LoginForm;
